<template>
  <div class="datafast-payment">
    <div
      v-if="!checkoutCreated"
      class="payment-form"
    >
      <h3>Información del Pago</h3>

      <div class="form-group">
        <label>Monto</label>
        <input
          v-model="paymentData.amount"
          type="text"
          disabled
        >
      </div>

      <div class="form-group">
        <label>Contrato</label>
        <input
          :value="displayContractCode"
          type="text"
          disabled
        >
      </div>

      <div class="form-group">
        <label>Usuario</label>
        <input
          :value="displayUserName"
          type="text"
          disabled
        >
      </div>

      <div
        v-if="isRecurring || saveCard"
        class="form-group recurring-info"
      >
        <div class="alert alert-info">
          <strong>Débito Automático Mensual</strong>
          <p class="mb-1">
            Tu tarjeta será guardada de forma segura (tokenizada)
            para realizar el cobro automático cada mes en el día que
            elijas.
          </p>
        </div>

        <p class="small text-muted mt-1 mb-2">
          El cobro automático se realizará el
          <strong>día 5 de cada mes</strong>
          .
        </p>

        <div
          class="d-flex align-items-center justify-content-between mt-1"
        >
          <span
            class="font-weight-bold"
            style="font-size: 14px; color: #334155"
          >
            Autorizo el débito automático mensual a esta tarjeta
          </span>
          <b-form-checkbox
            v-model="authorizeRecurring"
            name="datafast-authorize-recurring"
            switch
            class="m-0"
          />
        </div>
      </div>

      <div class="form-group">
        <div class="d-flex align-items-center justify-content-between">
          <span
            class="font-weight-bold"
            style="font-size: 14px; color: #334155"
          >
            Aceptar términos y condiciones y políticas de privacidad
          </span>

          <b-form-checkbox
            v-model="acceptTerms"
            name="datafast-accept"
            switch
            class="m-0"
          />
        </div>

        <div class="mt-1 small text-muted">
          Al continuar, aceptas los
          <a
            v-if="effectiveTermsUrl"
            :href="effectiveTermsUrl"
            class="terms-link"
            target="_blank"
            rel="noopener noreferrer"
            @click.stop.prevent="openLink(effectiveTermsUrl)"
          >
            Términos y Condiciones
          </a>
          <template v-else>
            Términos y Condiciones
          </template>
          y la
          <a
            v-if="effectivePrivacyUrl"
            :href="effectivePrivacyUrl"
            class="terms-link"
            target="_blank"
            rel="noopener noreferrer"
            @click.stop.prevent="openLink(effectivePrivacyUrl)"
          >
            Política de Privacidad
          </a>
          <template v-else>
            Política de Privacidad
          </template>
          .
        </div>
      </div>

      <button
        :disabled="loading || !isFormValid || checkoutCreated"
        class="btn btn-primary"
        @click="createCheckout"
      >
        {{ loading ? 'Creando...' : 'Procesar Pago con DataFast' }}
      </button>
    </div>

    <div
      v-else-if="checkoutCreated && !paymentProcessed"
      class="payment-widget"
    >
      <h3>Datos de la Tarjeta</h3>
      <p>Monto: ${{ checkoutData.amount }} {{ checkoutData.currency }}</p>
      <form
        :action="currentPageUrl"
        class="paymentWidgets"
        data-brands="VISA MASTER AMEX DINERS DISCOVER"
      />
      <div class="payment-actions">
        <button
          class="btn btn-secondary"
          @click="cancelPayment"
        >
          Cancelar
        </button>
      </div>
    </div>

    <div
      v-else-if="paymentProcessed"
      class="payment-result"
    >
      <div
        v-if="paymentResult.success"
        class="success"
      >
        <h3>Pago Exitoso</h3>
        <p>{{ paymentResult.message }}</p>
        <p>Código: {{ paymentResult.code }}</p>
      </div>
      <div
        v-else
        class="error"
      >
        <h3>Pago Rechazado</h3>
        <p>{{ paymentResult.message }}</p>
      </div>
      <div class="payment-actions-result">
        <button
          v-if="paymentResult.success"
          class="btn btn-success"
          @click="goToHome"
        >
          Ir al Inicio
        </button>
        <button
          class="btn btn-primary"
          @click="resetPayment"
        >
          {{ paymentResult.success ? 'Otro Pago' : 'Reintentar' }}
        </button>
      </div>
    </div>

    <div
      v-if="error"
      class="error-message"
    >
      <h4>Error</h4>
      <p>{{ error }}</p>
    </div>

    <b-overlay
      :show="loading"
      rounded="sm"
      no-wrap
    >
      <template #overlay>
        <div class="text-center p-4">
          <b-spinner
            variant="primary"
            style="width: 3rem; height: 3rem"
          />
          <p class="mt-3 mb-2 font-weight-bold">
            {{
              processingResult
                ? 'Verificando estado del pago...'
                : 'Procesando...'
            }}
          </p>
          <p
            v-if="processingResult"
            class="text-muted small"
          >
            Esto puede tomar unos segundos
          </p>
          <b-button
            v-if="loading && processingResult"
            variant="outline-secondary"
            size="sm"
            class="mt-2"
            @click="cancelPayment"
          >
            Cancelar
          </b-button>
        </div>
      </template>
    </b-overlay>
  </div>
</template>

<script>
import datafastMixin from '@/mixins/datafastMixin'
import datafastResultMixin from '@/mixins/datafastResultMixin'
import {
    showDataFastError,
    showDataFastToastError,
    showPaymentResult,
} from '@/services/datafastNotificationService'
import { validateRecurringPayment } from '@/utils/datafastTokenManager'
import { BButton, BFormCheckbox, BOverlay, BSpinner } from 'bootstrap-vue'

export default {
    name: 'DataFastPayment',
    components: { BButton, BOverlay, BSpinner, BFormCheckbox },
    mixins: [datafastMixin, datafastResultMixin],
    props: {
        contract: { type: Object, default: () => ({}), required: true },
        isRecurring: { type: Boolean, default: false },
        saveCard: { type: Boolean, default: false },
        /** URL de términos y condiciones. Si no se pasa, se usa VUE_APP_DATAFAST_TERMS_URL */
        termsUrl: { type: String, default: '' },
        /** URL de políticas de privacidad. Si no se pasa, se usa VUE_APP_DATAFAST_PRIVACY_URL */
        privacyUrl: { type: String, default: '' },
    },
    data() {
        return {
            loading: false,
            error: null,
            checkoutCreated: false,
            paymentProcessed: false,
            acceptTerms: false,
            authorizeRecurring: false,
            billingDay: 5,
            userData: null,
            paymentData: {
                amount: 0,
                idcontrato: null,
                idusuario: null,
                items: [],
            },
            checkoutData: {
                checkoutId: null,
                checkoutUrl: null,
                paymentId: null,
                amount: 0,
                currency: 'USD',
                entityId: null,
                environment: 'TEST',
            },
            paymentResult: {
                success: false,
                message: '',
                code: '',
            },
            paymentRetryAttempted: false,
            processingResult: false,
            lastResourcePath: null,
        }
    },
    computed: {
        isFormValid() {
            return (
                this.paymentData.amount > 0 &&
                this.paymentData.idcontrato &&
                this.paymentData.idusuario &&
                this.acceptTerms
            )
        },
        currentPageUrl() {
            return window.location.href.split('?')[0]
        },
        effectiveTermsUrl() {
            const url =
                this.termsUrl || process.env.VUE_APP_DATAFAST_TERMS_URL || ''
            return String(url).trim() || ''
        },
        effectivePrivacyUrl() {
            const url =
                this.privacyUrl ||
                process.env.VUE_APP_DATAFAST_PRIVACY_URL ||
                ''
            return String(url).trim() || ''
        },
        displayContractCode() {
            return this.contract?.codigo || this.contract?.nombre || ''
        },
        displayUserName() {
            return (
                this.userData?.fullName ||
                this.userData?.name ||
                this.userData?.email ||
                ''
            )
        },
    },
    mounted() {
        this.userData = JSON.parse(localStorage.getItem('userData'))
        this.initializePaymentData()
        this.checkPaymentResult()
        sessionStorage.setItem('datafast_payment_component_active', 'true')
    },
    beforeDestroy() {
        sessionStorage.removeItem('datafast_payment_component_active')
    },
    methods: {
        openExternalUrl(url) {
            if (url && typeof url === 'string') {
                window.open(url, '_blank', 'noopener,noreferrer')
            }
        },
        /** Abre URL en nueva pestaña (visualizar en lugar de descargar para .doc/.docx); si el navegador bloquea el popup, abre en la misma pestaña */
        openLink(url) {
            const u = url && typeof url === 'string' ? url.trim() : ''
            if (!u) return
            // Para .doc/.docx usar visor online y que se abra en pestaña sin descargar
            const lower = u.toLowerCase()
            const openUrl =
                lower.endsWith('.docx') || lower.endsWith('.doc')
                    ? `https://docs.google.com/viewer?url=${encodeURIComponent(
                          u
                      )}`
                    : u
            const w = window.open(openUrl, '_blank', 'noopener,noreferrer')
            if (!w || w.closed) {
                window.location.href = openUrl
            }
        },
        initializePaymentData() {
            let total = 0
            ;(this.contract?.selectedItems || []).forEach((i) => {
                total += parseFloat(i.saldo || 0)
            })

            this.paymentData = {
                amount: this.roundNumber(total, 2),
                idcontrato: this.contract.idcontrato,
                idusuario: this.userData?.id || null,
                items: this.contract.selectedItems || [],
                ruc: this.contract.ruc || null,
                email: this.contract.email || this.userData?.email || null,
                telefono: this.contract.telefono || null,
                direccion: this.contract.direccion || null,
                nombre: this.contract.nombre || this.contract.apenom || null,
                codigo: this.contract.codigo || null,
            }
        },

        async createCheckout(isAutoRetry = false) {
            if (!this.acceptTerms) {
                showDataFastToastError(
                    this,
                    'Debe aceptar los Términos y Condiciones'
                )
                return
            }

            if (
                (this.isRecurring || this.saveCard) &&
                !this.authorizeRecurring
            ) {
                showDataFastToastError(
                    this,
                    'Debe autorizar el débito automático mensual'
                )
                return
            }

            if (!isAutoRetry) {
                this.paymentRetryAttempted = false
            }

            const validation = validateRecurringPayment(this.paymentData)
            if (!validation.valid) {
                showDataFastError(
                    this,
                    validation.errors.join(', '),
                    'Error de Validación'
                )
                return
            }

            this.loading = true
            this.error = null

            try {
                this.removeDataFastScripts()

                const requestData = { ...this.paymentData }
                if (this.isRecurring || this.saveCard) {
                    requestData.subscribe = true
                    requestData.dia = this.billingDay
                }

                const resp = await this.$http.post(
                    '/datafast/checkout/create',
                    requestData
                )
                const d = resp?.data?.data || {}

                this.checkoutData = {
                    checkoutId:
                        d.checkoutId || d.checkout_id || d.reference || null,
                    checkoutUrl: d.checkoutUrl || d.checkout_url || null,
                    paymentId: d.paymentId || d.id || null,
                    amount: d.amount ?? this.paymentData.amount,
                    currency: d.currency || 'USD',
                    entityId:
                        d.entityId ||
                        process.env.VUE_APP_DATAFAST_ENTITY_ID ||
                        null,
                    environment:
                        d.environment === 'P' || d.environment === 'PROD'
                            ? 'PROD'
                            : 'TEST',
                }

                this.validateDataFastId(
                    this.checkoutData.checkoutId,
                    'checkoutId'
                )

                this.saveDataFastSession('datafast_payment', {
                    checkoutData: this.checkoutData,
                    paymentData: this.paymentData,
                    handledByComponent: true,
                    savedAt: new Date().toISOString(),
                })

                this.setDataFastWidgetOptions({
                    submitLabel: 'Pagar',
                    cardHolderName: '',
                })

                this.checkoutCreated = true
                await this.$nextTick()

                const sdkReady = await this.initializeDataFastSDK(
                    this.checkoutData.checkoutId,
                    this.checkoutData.environment,
                    this.checkoutData.checkoutUrl
                )
                if (!sdkReady) {
                    this.checkoutCreated = false
                    this.loading = false
                    return
                }
            } catch (e) {
                this.error = this.handleDataFastError(
                    e,
                    'Error al crear el checkout'
                )
                showDataFastError(this, this.error, 'Error')
            } finally {
                this.loading = false
            }
        },

        checkPaymentResult() {
            const result = this.checkDataFastURLParams('datafast_payment')

            if (result) {
                const { checkoutData, paymentData } = result.savedData
                this.checkoutData = checkoutData
                this.paymentData = paymentData

                this.processPaymentResult(result.resourcePath)
                this.cleanDataFastURL()
            }
        },

        async processPaymentResult(resourcePath) {
            if (
                this.processingResult ||
                this.lastResourcePath === resourcePath
            ) {
                return
            }

            this.processingResult = true
            this.lastResourcePath = resourcePath
            this.loading = true
            this.checkoutCreated = false

            try {
                const timeoutPromise = new Promise((_, reject) =>
                    setTimeout(() => {
                        reject(
                            new Error(
                                'Timeout: El servidor no respondió a tiempo'
                            )
                        )
                    }, 30000)
                )

                const requestPromise = this.$http.post(
                    '/datafast/checkout/process',
                    {
                        paymentId: this.checkoutData.paymentId,
                        resourcePath: decodeURIComponent(resourcePath),
                        checkoutId: this.checkoutData.checkoutId,
                    }
                )

                const resp = await Promise.race([
                    requestPromise,
                    timeoutPromise,
                ])

                if (resp?.data?.success) {
                    const d = resp.data.data || {}
                    const subscription = d.subscription || null

                    this.paymentResult = {
                        success: d.status === 'approved',
                        message: d.message || 'Transacción procesada',
                        code: d.code || '',
                        token: subscription
                            ? subscription.registrationId
                            : null,
                        cardBrand: null,
                        lastDigits: null,
                    }
                    this.paymentProcessed = true

                    this.cleanDataFastSession('datafast_payment')

                    if (this.paymentResult.success) {
                        this.$emit('payment-success', {
                            ...this.paymentResult,
                            isRecurring: this.isRecurring || this.saveCard,
                            subscriptionCreated: subscription
                                ? subscription.created
                                : false,
                        })
                    } else {
                        this.$emit('payment-declined', {
                            ...this.paymentResult,
                        })
                    }

                    showPaymentResult(
                        this,
                        this.paymentResult.success,
                        this.paymentResult.message,
                        this.paymentResult.code,
                        subscription
                    )
                } else {
                    throw new Error(
                        resp?.data?.error || 'Error al procesar pago'
                    )
                }
            } catch (e) {
                this.error = this.handleDataFastError(
                    e,
                    'Error al procesar pago'
                )
                const errorCode = this.extractDataFastErrorCode(e)

                if (errorCode === '200.300.404') {
                    this.checkoutCreated = false
                    this.paymentProcessed = false
                    this.cleanupDataFast('datafast_payment')
                    if (!this.paymentRetryAttempted) {
                        this.paymentRetryAttempted = true
                        setTimeout(() => {
                            this.createCheckout(true)
                        }, 0)
                    }
                }

                showDataFastError(
                    this,
                    this.error,
                    'Error al Procesar Pago',
                    errorCode
                )

                this.$emit('payment-error', {
                    error: this.error,
                    code: errorCode,
                })
            } finally {
                this.loading = false
                this.processingResult = false
            }
        },

        cancelPayment() {
            this.checkoutCreated = false
            this.paymentProcessed = false
            this.error = null

            this.cleanupDataFast('datafast_payment')

            const el = document.getElementById('datafast-widget')
            if (el) el.innerHTML = ''
        },

        resetPayment() {
            this.checkoutCreated = false
            this.paymentProcessed = false
            this.acceptTerms = false
            this.error = null
            this.paymentResult = { success: false, message: '', code: '' }

            this.cleanupDataFast('datafast_payment')

            const el = document.getElementById('datafast-widget')
            if (el) el.innerHTML = ''
            this.initializePaymentData()
        },

        goToHome() {
            // Intentionally empty
        },

        getCardHolderNameFromUser() {
            const isTestEnv =
                (
                    process.env.VUE_APP_DATAFAST_ENVIRONMENT || ''
                ).toUpperCase() === 'TEST'
            const testHolder = process.env.VUE_APP_DATAFAST_TEST_CARD_HOLDER
            if (isTestEnv && testHolder) {
                const normalizedTest = testHolder
                    .toUpperCase()
                    .replace(/[^A-Z\s]/g, '')
                    .replace(/\s+/g, ' ')
                    .trim()
                const words = normalizedTest.split(' ').filter(Boolean)
                const isValid =
                    normalizedTest.length >= 2 &&
                    words.every((word) => word.length >= 2)
                return isValid ? normalizedTest : ''
            }

            const candidate =
                this.userData?.fullName ||
                this.userData?.name ||
                `${this.userData?.firstName || ''} ${
                    this.userData?.lastName || ''
                }`.trim()
            if (!candidate) return ''

            const normalized = candidate
                .toUpperCase()
                .replace(/[^A-Z\s]/g, '')
                .replace(/\s+/g, ' ')
                .trim()
            const words = normalized.split(' ').filter(Boolean)
            const isValid = isTestEnv
                ? normalized.length >= 2 &&
                  words.every((word) => word.length >= 2)
                : words.length >= 2 && words.every((word) => word.length >= 2)
            return isValid ? normalized : ''
        },
    },
}
</script>

<style scoped>
.datafast-payment {
    max-width: 520px;
    margin: 0 auto;
    padding: 20px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background: #fff;
}
.form-group {
    margin-bottom: 16px;
}
.form-group label {
    display: block;
    margin-bottom: 6px;
    font-weight: 600;
    color: #334155;
    font-size: 14px;
}
/* Enlaces de términos y privacidad (están dentro de .form-group pero no dentro de label) */
.form-group .terms-link {
    color: #2563eb !important;
    text-decoration: underline;
    font-weight: 600;
    cursor: pointer;
}
.form-group .terms-link:hover {
    color: #1d4ed8 !important;
    text-decoration: underline;
}
.form-group input {
    width: 100%;
    padding: 12px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 15px;
    background: #f8fafc;
    color: #1e293b;
}
.btn {
    padding: 12px 24px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 15px;
    font-weight: 600;
    margin-right: 10px;
    transition: all 0.2s;
}
.btn-primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: #fff;
}
.btn-primary:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}
.btn-secondary {
    background: #64748b;
    color: #fff;
}
.btn-success {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: #fff;
}
.btn-success:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
}
.btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
.payment-widget,
.payment-result {
    text-align: center;
}
.success {
    color: #10b981;
}
.success h3::before {
    content: '✓ ';
}
.error {
    color: #ef4444;
}
.error h3::before {
    content: '✗ ';
}
.error-message {
    background: #fef2f2;
    color: #991b1b;
    padding: 16px;
    border-radius: 8px;
    margin-top: 16px;
}
.payment-actions {
    margin-top: 20px;
}
.payment-actions-result {
    margin-top: 24px;
    display: flex;
    gap: 12px;
    justify-content: center;
    flex-wrap: wrap;
}
.recurring-info .alert {
    padding: 16px;
    border-radius: 8px;
    margin: 0;
}
.alert-info {
    background: #eff6ff;
    color: #1e40af;
    border: 1px solid #bfdbfe;
}
.alert-info strong {
    display: block;
    margin-bottom: 6px;
}
.alert-info p {
    margin: 0;
    font-size: 14px;
}
</style>
